import { useGameStore } from "@/store/gameStore";

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "https://api-dev.ispyfr.com";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface FetchOptions {
  method?: string;
  body?: unknown;
  token?: string;
  query?: Record<string, string | undefined>;
}

export async function fetchJson<T>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const { method = "GET", body, token, query } = options;

  let url = `${BASE_URL}${path}`;
  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) params.set(k, v);
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const json = await res.json();
  if (!res.ok) {
    throw new ApiError(res.status, json.error ?? json.message ?? res.statusText);
  }
  return json as T;
}

/** Fetch with auto-resolved token from the session store */
export function fetchAuthed<T>(
  gameId: string,
  path: string,
  options: Omit<FetchOptions, "token"> = {},
): Promise<T> {
  const session = useGameStore.getState().getSession(gameId);
  return fetchJson<T>(path, { ...options, token: session?.playerToken });
}

/** Upload a blob directly to a presigned S3 URL */
export async function uploadImage(
  presignedUrl: string,
  blob: Blob,
): Promise<void> {
  const res = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": "image/jpeg" },
    body: blob,
  });
  if (!res.ok) {
    throw new ApiError(res.status, "Image upload failed");
  }
}
